import dagre from "@dagrejs/dagre";
import {defaults} from "./Defaults";
import {generateHeaderedBox, getNodeHeight, standardLink} from "./Elements";
import {dia, shapes} from "@joint/core";

export const calculateLayout = (archiveObjects) => {
    const g = new dagre.graphlib.Graph();
    g.setGraph({
        marginx: defaults.paperMarginX,
        marginy: defaults.paperMarginY,
        rankdir: defaults.graphRankDir,
        ranker: defaults.graphRanker,
        align: defaults.graphAlign,
        nodesep: defaults.nodeSep,
    });
    g.setDefaultEdgeLabel(function () {
        return {};
    });
    const nodes = new Map();
    const edges = new Map();
    archiveObjects.forEach((ao) => {
        const node = new ObjectNode(ao.name, ao.navigationLabel);
        if (!nodes.has(node.name)) {
            nodes.set(node.name, node);
            const ports = new Set();
            ao.parentObjects.forEach((po) => {
                let portName = po.foreignKey;
                if (po.name === ao.name) {
                    portName += '(self)'
                }
                ports.add(portName);
            });
            g.setNode(node.name, {
                label: node.title,
                width: defaults.nodeWidth,
                height: getNodeHeight(ports),
                ports: Array.from(ports)
            });
        }
        ao.childObjects.forEach((co) => {
            const edge = new ObjectReference(node.name, co.name, co.foreignKey);
            if (!edges.has(co.name)) {
                edges.set(edge.getHash(), edge);
                g.setEdge(edge.from, edge.to, {foreignKey: edge.foreignKey});
            }
        });
    });
    dagre.layout(g);
    return g;
}

export const drawGraph = (width, height, layout) => {
    const namespace = shapes;
    const graph = new dia.Graph({}, {cellNamespace: namespace});

    const {maxHeight, maxWidth} = addNodesToGraph(layout, graph, height, width);
    addEdgesToGraph(layout, graph);

    const paper = new dia.Paper({
        el: document.getElementById('joint_paper'),
        model: graph,
        width: maxWidth,
        height: maxHeight,
        background: {color: defaults.paperBackgroundColor},
        cellViewNamespace: namespace,
        linkView: dia.LinkView.extend({
            mouseover: function (evt) {
                const localPaper = this.paper;
                const localPoint = getScaledCoordinates(evt, this.paper);
                const linkModel = this.model;
                const cellUpdates = {
                    'header/stroke': defaults.elementActiveBorderColor,
                    'header/strokeWidth': defaults.elementActiveStrokeWidth,
                    'body/stroke': defaults.elementActiveBorderColor,
                    'body/strokeWidth': defaults.elementActiveStrokeWidth
                }
                const linkUpdates = {
                    'line/stroke': defaults.linkActiveColor,
                    'line/strokeWidth': defaults.elementActiveStrokeWidth,
                    'line/strokeDasharray': null
                }
                const portUpdates = {
                    'portBody/strokeWidth': defaults.elementActiveStrokeWidth
                };
                applyUpdatesOnLinkModel(linkModel, linkUpdates, cellUpdates, portUpdates);
                bringNonVisibleCloser(localPaper, linkModel, localPoint);
            },
            mouseout: function (evt) {
                const linkModel = this.model;
                const localPaper = this.paper;
                const cellUpdates = {
                    'header/stroke': defaults.elementPassiveBorderColor,
                    'header/strokeWidth': defaults.elementPassiveStrokeWidth,
                    'body/stroke': defaults.elementPassiveBorderColor,
                    'body/strokeWidth': defaults.elementPassiveStrokeWidth
                }
                const linkUpdates = {
                    'line/stroke': defaults.linkPassiveColor,
                    'line/strokeWidth': defaults.elementPassiveStrokeWidth,
                    'line/strokeDasharray': defaults.linkPassiveStrokeDasharray
                }
                const portUpdates = {
                    'portBody/strokeWidth': defaults.elementPassiveStrokeWidth
                };
                applyUpdatesOnLinkModel(linkModel, linkUpdates, cellUpdates, portUpdates);
                removeNonVisibleClones(localPaper);
            }
        })
    });
    paper.scale(defaults.paperScaleX, defaults.paperScaleY);
}

const addNodesToGraph = (layout, graph, height, width) => {
    let maxHeight = height;
    let maxWidth = width;
    layout.nodes().forEach((nodeKey) => {
        const node = layout.node(nodeKey);
        const nodeRightX = (node.x + node.width * 0.5 + defaults.paperMarginX) * defaults.paperScaleX;
        if (nodeRightX > maxWidth) {
            maxWidth = nodeRightX;
        }
        const nodeBottomY = (node.y + node.height * 0.5 + defaults.paperMarginY) * defaults.paperScaleY;
        if (nodeBottomY > maxHeight) {
            maxHeight = nodeBottomY;
        }
        const headeredBox = generateHeaderedBox(nodeKey, node.x, node.y, node.ports);
        headeredBox.addTo(graph);
    });
    return {maxHeight, maxWidth};
}

const addEdgesToGraph = (layout, graph) => {
    layout.edges().forEach((edge) => {
        const source = edge.v;
        const target = edge.w;
        if (source !== target) {
            const edgeConfig = layout.edge(edge);
            const link = standardLink(source, target, edgeConfig.foreignKey);
            link.router(defaults.linkRouter, {
                startDirections: ['left'],
                endDirections: ['bottom'],
                excludeTypes: ['custom.FloatingRectangle'],
            });
            link.addTo(graph);
        }
    });
}

const getScaledCoordinates = (evt, paper) => {
    const paperClientRect = paper.el.getBoundingClientRect();
    const scale = paper.scale(); // returns {sx, sy}

    const x = ((evt.clientX - paperClientRect.left) / scale.sx) + defaults.linkTooltipOffset;
    const y = ((evt.clientY - paperClientRect.top) / scale.sy) + defaults.linkTooltipOffset;

    return {x, y};
}

const bringNonVisibleCloser = (localPaper, linkModel, position) => {
    const sourceElement = linkModel.getSourceElement();
    const targetElement = linkModel.getTargetElement();
    const nonVisibleElements = [];
    [sourceElement, targetElement].forEach((element) => {
        const isVisible = isElementVisibleOnPaper(localPaper, element);
        if (!isVisible) {
            nonVisibleElements.push(element.attr('headerText/text'));
        }
    });
    if (nonVisibleElements.length > 0) {
        if (nonVisibleElements.length > 1) {
            nonVisibleElements.reverse();
            const spliceStart = nonVisibleElements.length - 1;
            nonVisibleElements.splice(spliceStart, 0, "<<");
        }
        const title = nonVisibleElements.join(" ");
        const clone = generateFloatingBox(title, position.x, position.y);
        clone.prop('isClone', true);
        clone.addTo(localPaper.model);
    }
}


const removeNonVisibleClones = (localPaper) => {
    const graph = localPaper.model;
    const forRemoval = [];
    graph.getCells().forEach((cell) => {
        if (cell.isElement()) {
            if (cell.prop('isClone')) {
                forRemoval.push(cell);
            }
        }
    });
    graph.removeCells(forRemoval);
}

const applyUpdatesOnLinkModel = (linkModel, linkUpdates, cellUpdates, portUpdates) => {
    const sourceElement = linkModel.getSourceElement();
    const targetElement = linkModel.getTargetElement();
    applyUpdatesOnLinkElement(linkModel, linkUpdates);
    const elements = [sourceElement, targetElement];
    elements.forEach((element) => {
        if (element) {
            applyUpdatesOnLinkElement(element, cellUpdates);
        }
    });
    const sourcePortId = linkModel.get('source').port;
    if (sourcePortId) {
        Object.keys(portUpdates).forEach((key) => {
            sourceElement.portProp(sourcePortId, 'attrs/' + key, portUpdates[key]);
        });
    }
}

const applyUpdatesOnLinkElement = (element, updates) => {
    Object.keys(updates).forEach((key) => {
        const val = updates[key];
        element.attr(key, val);
    });
}

const isElementVisibleOnPaper = (paper, element) => {
    const bbox = element.getBBox();
    const scale = paper.scale(); // {sx, sy}
    const translate = paper.translate(); // {tx, ty}

    // Convert model coordinates to screen coordinates
    const screenX = bbox.x * scale.sx + translate.tx;
    const screenY = bbox.y * scale.sy + translate.ty;
    const screenWidth = bbox.width * scale.sx;
    const screenHeight = bbox.height * scale.sy;

    // Get paper's position on the page
    const paperRect = paper.el.getBoundingClientRect();

    // Calculate model's position relative to the viewport
    const modelLeft = paperRect.left + screenX;
    const modelTop = paperRect.top + screenY;

    // Check against viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    return (
        modelLeft + screenWidth > 0 &&
        modelTop + screenHeight > 0 &&
        modelLeft < viewportWidth &&
        modelTop < viewportHeight
    );
}

function generateFloatingBox(title, x, y) {
    const tempSpan = document.createElement('span');
    tempSpan.style.font = `bold ${defaults.floatingBoxFontSize} Arial`;
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.textContent = title;
    document.body.appendChild(tempSpan);

    const textWidth = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);

    const padding = defaults.floatingBoxPadding;
    const width = Math.max(defaults.floatingBoxMinWidth, textWidth + padding);

    return new shapes.custom.FloatingRectangle({
        position: {x: x, y: y},
        size: {width, height: defaults.floatingBoxHeight},
        attrs: {
            label: {text: title}
        }
    });
}

class ObjectNode {
    constructor(name, title) {
        this.name = name;
        this.title = title;
    }
}


class ObjectReference {
    constructor(from, to, foreignKey) {
        this.from = from;
        this.to = to;
        this.foreignKey = foreignKey;
    }

    getHash() {
        return `${this.from.name}-${this.to.name}-${this.foreignKey}`;
    }
}
